/*
  PINGO STUDIOS — GSAP Motion Layer
  Adopted from docs/FRONTEND-STACK.md (2026-06-15). GSAP owns ALL motion.

  Loaded `defer` AFTER GSAP core + ScrollTrigger (also `defer`) in
  layout/theme.liquid + layout/password.liquid. Deferred classic scripts run in
  document order, so `gsap` / `ScrollTrigger` globals are guaranteed present by
  the time this runs.

  Owns (migrated off the old IntersectionObserver / CSS-transition scripts on
  2026-06-15 — pingo-wipe.js G15 and pingo-image-fade.js G16 deleted):
    • G15 motion palette  — pingo-{wipe,fade-up,blur-in,scale-soft,slide-in,
                            typewriter} arrival animations, scroll-triggered.
    • G16 hero image fade — [data-pingo-hero] overlay, image-load-triggered.
    • Pattern drift     — [data-pingo-pattern-motion="drift"] ambient loop on
                          the pingo-pattern block (DS v4 Phase 1, mobile-inclusive).
    • Button feedback   — (DS v4 Phase 5 / 5B) hover-lift + press-into-shadow on
                          .button / .button-secondary, via document-level pointer
                          delegation (one listener set; covers dynamic buttons).
    • Card hover        — (DS v4 Phase 5 / 5C) lift + subtle sticker-peel tilt on
                          .product-card (tweens .product-card__content so the
                          surface + sticker shadow rise together). Desktop-hover
                          only; same pointer-delegation pattern.
    • Headline reveal   — (DS v4 Phase 5 / 5D) SplitText word rise+fade on
                          .pingo-headline, scroll-triggered once. Runs after
                          document.fonts.ready (Caslon measures correctly).
    • Line-art draw-on  — (DS v4 Phase 5 / 5E) DrawSVG self-draws stroked SVG
                          geometry inside .pingo-draw on scroll-enter, once.
                          Opt-in carrier = the pingo-svg block (class_name).
    • Curtain reveal    — (DS v4 Phase 5 / 5F) .pingo-curtain clip-path rise on
                          section-entrance (NOT a page transition). Opt-in via
                          class_name; scroll-triggered once.

  The hidden / covered INITIAL states live in assets/pingo-custom.css, gated on
  .pingo-js (FOUC-safe + no-JS-safe). This file only animates TO the resting
  state. prefers-reduced-motion: we create no tweens — the CSS reduced-motion
  rules already show content / drop the overlay instantly.
*/
(function () {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger) {
    if (window.console) console.warn('[pingo-animations] GSAP/ScrollTrigger not found — motion layer skipped.');
    return;
  }

  // Register ScrollTrigger + the free 3.13 plugins that loaded (DrawSVG line-art,
  // SplitText headline reveals). Pass only what's actually present.
  gsap.registerPlugin.apply(gsap, [ScrollTrigger, window.DrawSVGPlugin, window.SplitText].filter(Boolean));

  // "Frozen first scroll" fix. iOS/Android collapse the browser URL bar on the
  // FIRST scroll from the top — a window `resize` (height-only). ScrollTrigger's
  // default autoRefreshEvents include `resize`, so it runs a full refresh()
  // (recomputing EVERY trigger's geometry = a forced synchronous layout) right in
  // the middle of that first scroll, which reads as the page freezing for a beat
  // before it moves. Chrome/desktop never shows it (no URL-bar collapse).
  // ignoreMobileResize makes ScrollTrigger skip those mobile-chrome height resizes
  // (genuine orientation/width changes still refresh). See DECISIONS.md → Performance.
  ScrollTrigger.config({ ignoreMobileResize: true });

  // Horizon 4.x: the scroll CONTAINER differs by breakpoint (base.css). At ≥990px
  // html/body are locked (overflow:hidden) and `.page-wrapper` is the scroller;
  // BELOW 990px the WINDOW scrolls (`.page-wrapper` overflow:visible, its
  // clientHeight == scrollHeight — NOT a scroll viewport). ScrollTrigger defaults to
  // the window scroller, so point it at `.page-wrapper` ONLY on desktop. On mobile,
  // pinning it to the non-scrolling `.page-wrapper` made every scroll-triggered
  // reveal + viewport-gated ambient loop compute as already-passed → fire on LOAD
  // instead of on scroll (the motion effect was silently lost on mobile). Below
  // 990px we keep ST's window default (the real mobile scroller).
  var pingoScroller = document.querySelector('.page-wrapper');
  var pingoDesktop = window.matchMedia('(min-width: 990px)');
  function applyPingoScroller() {
    ScrollTrigger.defaults({ scroller: pingoScroller && pingoDesktop.matches ? pingoScroller : window });
  }
  applyPingoScroller();
  // Breakpoint crossing mid-session (e.g. tablet rotation) swaps the scroll
  // container — update the default + refresh so subsequently-bound triggers use the
  // right scroller. (Triggers already created keep their scroller; a reload is
  // cleanest if the viewport crosses 990px mid-session — a rare edge.)
  pingoDesktop.addEventListener('change', function () {
    applyPingoScroller();
    ScrollTrigger.refresh();
  });

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Scroll-enter trigger tuned to match the old IntersectionObserver
  // (threshold 0.15 + rootMargin bottom -40px): fire as the element's top
  // reaches ~90% down the viewport. once:true == the old single-fire/unobserve.
  var START = 'top 90%';

  // Marks an element whose tween has been created, so section:load rescans and
  // the initial document pass never double-bind the same node.
  var DONE = 'data-pingo-anim-done';

  // G15 palette — opacity/transform entries (wipe + typewriter handled below).
  var PALETTE = [
    { sel: '.pingo-fade-up',    from: { opacity: 0, y: 12 },               to: { opacity: 1, y: 0,                duration: 0.6, ease: 'power2.out' } },
    { sel: '.pingo-blur-in',    from: { opacity: 0, filter: 'blur(8px)' }, to: { opacity: 1, filter: 'blur(0px)', duration: 0.7, ease: 'power2.out' } },
    { sel: '.pingo-scale-soft', from: { opacity: 0, scale: 0.96 },         to: { opacity: 1, scale: 1,            duration: 0.6, ease: 'power2.out' } },
    { sel: '.pingo-slide-in',   from: { opacity: 0, x: -20 },              to: { opacity: 1, x: 0,                duration: 0.6, ease: 'power2.out' } }
  ];

  function bind(el) {
    if (el.hasAttribute(DONE)) return false;
    el.setAttribute(DONE, '');
    return true;
  }

  function animatePalette(root) {
    if (prefersReducedMotion) return; // CSS reduced-motion rules show content

    PALETTE.forEach(function (cfg) {
      root.querySelectorAll(cfg.sel).forEach(function (el) {
        if (!bind(el)) return;
        gsap.fromTo(el, cfg.from, Object.assign({}, cfg.to, {
          scrollTrigger: { trigger: el, start: START, once: true }
        }));
      });
    });

    // pingo-wipe — the inner child rises within an overflow:hidden wrapper
    // (preserves the exact pre-migration "wipe up" visual; clip-path on the
    // wrapper would zero its trigger geometry).
    root.querySelectorAll('.pingo-wipe').forEach(function (el) {
      if (!bind(el)) return;
      gsap.fromTo(el.children, { yPercent: 110 }, {
        yPercent: 0, duration: 0.7, ease: 'power2.inOut',
        scrollTrigger: { trigger: el, start: START, once: true }
      });
    });

    // pingo-typewriter — clip-path sweep with a stepped ease; honors the
    // per-element --pingo-typewriter-duration / --pingo-typewriter-steps vars.
    root.querySelectorAll('.pingo-typewriter').forEach(function (el) {
      if (!bind(el)) return;
      var cs = getComputedStyle(el);
      var durMs = parseFloat(cs.getPropertyValue('--pingo-typewriter-duration')) || 1200;
      var steps = parseInt(cs.getPropertyValue('--pingo-typewriter-steps'), 10) || 40;
      gsap.fromTo(el, { clipPath: 'inset(0 100% 0 0)' }, {
        clipPath: 'inset(0 0 0 0)',
        duration: durMs / 1000, ease: 'steps(' + steps + ')',
        scrollTrigger: { trigger: el, start: START, once: true }
      });
    });
  }

  // Motion presets — the settings-driven successor to the .pingo-* class palette
  // (DS §5: convert class-hooks into real controls). One initializer reads every
  // [data-pingo-anim] (emitted by snippets/pingo-motion-attrs.liquid from the
  // shared "Movimento" block settings) and applies the GSAP preset with the
  // merchant's knobs. Entrances (mode="in") fire once on scroll/load then
  // dispose (clearProps); the three ambient presets (mode="loop") run
  // viewport-gated (ScrollTrigger onToggle) + mobile-lightened. Transform/opacity
  // only, except blur-in (filter) which mobile-caps. Reduced-motion → no tween.
  function initMotionPresets(root) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('[data-pingo-anim]').forEach(function (el) {
      var p = el.getAttribute('data-pingo-anim');
      if (!p || p === 'none') return;
      if (!bind(el)) return;

      if (prefersReducedMotion) { gsap.set(el, { opacity: 1 }); return; }

      var mode   = el.getAttribute('data-pingo-anim-mode') || 'in';
      var dur    = parseFloat(el.getAttribute('data-pingo-anim-dur')) || 0.6;
      var delay  = parseFloat(el.getAttribute('data-pingo-anim-delay')) || 0;
      var bounce = el.getAttribute('data-pingo-anim-ease') !== 'smooth';
      var ease   = bounce ? 'back.out(1.6)' : 'power3.out';
      var amt    = parseFloat(el.getAttribute('data-pingo-anim-amt'));
      if (isNaN(amt)) amt = 24;

      if (mode === 'loop') {
        var f = isMobile ? 0.5 : 1, tween;
        if (p === 'float')      tween = gsap.to(el, { y: -8 * f, duration: 2.4, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true });
        else if (p === 'drift') tween = gsap.to(el, { x: 6 * f, y: -4 * f, rotation: 1.5 * f, duration: 3.6, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true });
        else                    tween = gsap.to(el, { rotation: 2.5 * f, duration: 2.8, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true }); // sway
        ScrollTrigger.create({
          trigger: el, start: 'top bottom', end: 'bottom top',
          onToggle: function (self) { self.isActive ? tween.resume() : tween.pause(); }
        });
        return;
      }

      // entrance presets
      var from = { opacity: 0 }, extra = {};
      if (p === 'fade-up')         from.y = amt;
      else if (p === 'slide-in')   from.x = -amt;
      else if (p === 'scale-soft') from.scale = 0.92;
      else if (p === 'pop')      { from.scale = 0.6; ease = bounce ? 'back.out(2.4)' : 'power3.out'; }
      else if (p === 'carimbo')  {
        // Stop-motion stamp: a stepped ease makes the transform + fade jump in
        // discrete frames (a stamp thudding down) rather than easing smoothly.
        // Frame count is merchant-controlled via data-pingo-anim-steps (default 5);
        // overrides the bounce/smooth ease this preset would otherwise use.
        from.scale = 0.9; from.rotation = -6; from.y = 14;
        var cSteps = parseInt(el.getAttribute('data-pingo-anim-steps'), 10);
        ease = 'steps(' + (cSteps > 0 ? cSteps : 5) + ')';
      }
      else if (p === 'blur-in')  { from.filter = 'blur(' + (isMobile ? 6 : 12) + 'px)'; extra.filter = 'blur(0px)'; }
      else if (p === 'wipe')     { from.opacity = 1; from.clipPath = 'inset(0 100% 0 0)'; extra.clipPath = 'inset(0 0% 0 0)'; }

      el.style.willChange = (p === 'blur-in') ? 'filter, opacity' : 'transform, opacity';
      var to = Object.assign({
        opacity: 1, x: 0, y: 0, scale: 1, rotation: 0,
        duration: dur, delay: delay, ease: ease,
        clearProps: 'transform,filter,clipPath,willChange'
      }, extra);
      if (el.getAttribute('data-pingo-anim-trigger') !== 'load') {
        to.scrollTrigger = { trigger: el, start: START, once: true };
      }
      gsap.fromTo(el, from, to);
    });
  }

  // G16 — hero overlay fade. GSAP can't target a ::before, so it animates the
  // --pingo-hero-overlay-opacity var the pseudo-element reads. Triggered when
  // the first <img> in the hero finishes loading (above-the-fold, not scroll).
  function initHeroFade(root) {
    root.querySelectorAll('[data-pingo-hero]').forEach(function (section) {
      if (section.hasAttribute(DONE)) return;

      function fade() {
        if (!bind(section)) return;
        if (prefersReducedMotion) {
          gsap.set(section, { '--pingo-hero-overlay-opacity': 0 });
          return;
        }
        gsap.to(section, { '--pingo-hero-overlay-opacity': 0, duration: 0.42, ease: 'power3.out' });
      }

      var imgs = section.querySelectorAll('img');
      if (!imgs.length) { fade(); return; }

      var fired = false;
      function tryFade() { if (!fired) { fired = true; fade(); } }
      imgs.forEach(function (img) {
        if (img.complete && img.naturalWidth > 0) {
          tryFade();
        } else {
          img.addEventListener('load', tryFade, { once: true });
          img.addEventListener('error', tryFade, { once: true });
        }
      });
    });
  }

  // Pattern drift (pingo-pattern block) — Tier-1 ambient. GPU transform on the
  // oversized .pingo-pattern__layer (clipped by the shell's overflow, so the
  // drift never reveals an edge). Mobile-INCLUSIVE per DS v4: runs lightened
  // (slower + smaller amplitude) on small screens rather than killed.
  // Viewport-gated (paused off-screen). reduced-motion → no tween.
  function initPatternDrift(root) {
    if (prefersReducedMotion) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('[data-pingo-pattern-motion="drift"]').forEach(function (host) {
      var layer = host.querySelector('.pingo-pattern__layer');
      if (!layer) return;
      if (!bind(host)) return;
      var amp = isMobile ? 1.5 : 3; // % of the oversized layer
      var dur = isMobile ? 14 : 9;  // slower (lighter) on mobile
      var tween = gsap.to(layer, {
        xPercent: amp, yPercent: -amp, duration: dur,
        ease: 'sine.inOut', repeat: -1, yoyo: true, paused: true
      });
      ScrollTrigger.create({
        trigger: host, start: 'top bottom', end: 'bottom top',
        onToggle: function (self) { self.isActive ? tween.play() : tween.pause(); }
      });
    });
  }

  // Mushroom sprout (pingo-mushrooms section) — Tier-1 ambient. CSS owns the
  // pingoSprout keyframe (up to 8 GPU-composited transforms, faithful to the DS
  // render); this only flips the viewport play-state gate so the loop pauses
  // off-screen. Mobile-INCLUSIVE (no mobile kill — decorative, cheap). CSS handles
  // reduced-motion (animation:none), so we no-op there.
  function initMushroomSprout(root) {
    if (prefersReducedMotion) return;
    root.querySelectorAll('.pingo-mushrooms[data-sprout]').forEach(function (host) {
      if (!bind(host)) return;
      ScrollTrigger.create({
        trigger: host, start: 'top bottom', end: 'bottom top',
        onToggle: function (self) {
          host.style.setProperty('--pingo-mush-play', self.isActive ? 'running' : 'paused');
        }
      });
    });
  }

  // 5B — Button hover-lift + press-into-shadow. GSAP-strict (owner directive:
  // "GSAP owns ALL motion" — no CSS :hover/:active path). Transform-only (GPU),
  // so the sticker shadow appears to compress as the button presses toward its
  // bottom-right offset corner — no box-shadow tween needed. Document-level
  // pointer delegation: one listener set binds every current + future button
  // (cart drawer, section:load) with zero per-element wiring. reduced-motion →
  // not wired at all; hover-lift gated on a real hover pointer (touch gets press
  // feedback only, since pointerover on touch is unreliable).
  function initButtonFeedback() {
    if (prefersReducedMotion) return;
    if (window.__pingoBtnFeedback) return; // delegation — bind once, ever
    window.__pingoBtnFeedback = true;

    var SEL = '.button, .button-secondary';
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function restY(btn) { return canHover && btn.matches(':hover') ? -2 : 0; }

    if (canHover) {
      document.addEventListener('pointerover', function (e) {
        var btn = e.target.closest && e.target.closest(SEL);
        if (!btn) return;
        gsap.to(btn, { y: -2, x: 0, scale: 1, duration: 0.24, ease: 'power2.out', overwrite: true });
      });
      document.addEventListener('pointerout', function (e) {
        var btn = e.target.closest && e.target.closest(SEL);
        if (!btn || btn.contains(e.relatedTarget)) return; // ignore moves onto own children
        gsap.to(btn, { y: 0, x: 0, scale: 1, duration: 0.3, ease: 'power2.out', overwrite: true });
      });
    }

    document.addEventListener('pointerdown', function (e) {
      var btn = e.target.closest && e.target.closest(SEL);
      if (!btn) return;
      // Presses toward the shadow corner.
      gsap.to(btn, { x: 2, y: 2, scale: 0.97, duration: 0.1, ease: 'power2.out', overwrite: true });
      // CF-3 — compress the sticker shadow 5px→3px on press. Only buttons whose
      // box-shadow reads --pingo-btn-sticker-shadow (the mint ATC, sticker badges)
      // react; for the rest this var is set but unused, so it's a no-op.
      btn.style.setProperty('--pingo-btn-sticker-shadow', 'var(--pingo-shadow-sticker-press)');
    });
    function release(e) {
      var btn = e.target.closest && e.target.closest(SEL);
      if (!btn) return;
      gsap.to(btn, { x: 0, y: restY(btn), scale: 1, duration: 0.34, ease: 'back.out(2)', overwrite: true });
      btn.style.removeProperty('--pingo-btn-sticker-shadow'); // CF-3 — restore the 5px rest sticker
    }
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);
  }

  // 5C — Product-card hover lift + subtle sticker-peel tilt. GSAP-strict, same
  // document-level pointer delegation as 5B. Tweens .product-card__content (the
  // visible surface that carries the sticker shadow + radius) so the card and
  // its shadow rise together — a uniform lift, not a shadow detach. A per-card
  // __pingoHovering flag dedupes the bubbling pointerover (fires once per card,
  // not once per descendant). Desktop hover only (cards on touch navigate on
  // tap — no lift); reduced-motion → not wired.
  function initCardHover() {
    if (prefersReducedMotion) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.__pingoCardHover) return; // delegation — bind once, ever
    window.__pingoCardHover = true;

    document.addEventListener('pointerover', function (e) {
      var card = e.target.closest && e.target.closest('.product-card');
      if (!card || card.__pingoHovering) return;
      var surface = card.querySelector('.product-card__content');
      if (!surface) return;
      card.__pingoHovering = true;
      gsap.to(surface, { y: -4, rotation: -0.6, scale: 1.01, duration: 0.3, ease: 'power2.out', overwrite: true });
    });
    document.addEventListener('pointerout', function (e) {
      var card = e.target.closest && e.target.closest('.product-card');
      if (!card || card.contains(e.relatedTarget)) return; // still within the card
      var surface = card.querySelector('.product-card__content');
      if (!surface) return;
      card.__pingoHovering = false;
      gsap.to(surface, { y: 0, rotation: 0, scale: 1, duration: 0.4, ease: 'power2.out', overwrite: true });
    });
  }

  // 5D — SplitText Caslon headline reveal. Opt-in via .pingo-headline (placed
  // through the Typography Surface class_name hook in template JSON, so zero
  // native edits). Splits the inner heading into words; each word rises + fades
  // in on scroll-enter, once, with a stagger. Caslon is a self-hosted custom
  // font, so we split only AFTER document.fonts.ready — splitting against the
  // fallback metrics would wrap words on the wrong lines, then reflow when
  // Caslon swaps in. SplitText absent (plugin didn't load) → reveal without
  // splitting. Mobile-inclusive but lightened (smaller rise, tighter stagger).
  // The hidden initial state (visibility:hidden) + reduced-motion reveal live in
  // pingo-custom.css gated on .pingo-js. Restraint per §1.5: editorial headlines
  // only (below the fold) — never the LCP H1 or dense commerce UI.
  function animateHeadlines(root) {
    if (prefersReducedMotion) return; // CSS reveals the headline, no tween
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    root.querySelectorAll('.pingo-headline').forEach(function (el) {
      if (!bind(el)) return;
      if (!window.SplitText) { gsap.set(el, { autoAlpha: 1 }); return; }
      var target = el.querySelector('h1, h2, h3, h4, h5, h6, p') || el;
      var split = new SplitText(target, { type: 'words', wordsClass: 'pingo-headline__word' });
      gsap.set(el, { autoAlpha: 1 }); // reveal the container; words hold the hidden state
      gsap.from(split.words, {
        yPercent: isMobile ? 60 : 100,
        opacity: 0,
        duration: isMobile ? 0.5 : 0.7,
        ease: 'power3.out',
        stagger: isMobile ? 0.03 : 0.05,
        scrollTrigger: { trigger: el, start: START, once: true }
      });
    });
  }

  // 5E — DrawSVG line-art draw-on (the brand's hand-drawn signature). Opt-in via
  // .pingo-draw on any element wrapping inline STROKED SVG geometry — the natural
  // carrier is the pingo-svg sticker block (set class_name "pingo-draw" + upload a
  // line-art SVG). Each stroked shape self-draws on scroll-enter, once, staggered.
  // DrawSVGPlugin absent → art left fully drawn (no-op). reduced-motion → no tween
  // (art stays drawn). Mobile-inclusive. Filled-only shapes are harmless (drawSVG
  // sets stroke-dash; with no stroke colour nothing animates).
  function initDrawSVG(root) {
    if (prefersReducedMotion || !window.DrawSVGPlugin) return;
    root.querySelectorAll('.pingo-draw').forEach(function (host) {
      if (!bind(host)) return;
      var strokes = host.querySelectorAll('path, line, polyline, polygon, circle, rect, ellipse');
      if (!strokes.length) return;
      gsap.set(strokes, { drawSVG: 0 });
      gsap.to(strokes, {
        drawSVG: '100%', duration: 1.1, ease: 'power2.out', stagger: 0.15,
        scrollTrigger: { trigger: host, start: START, once: true }
      });
    });
  }

  // 5F — Curtain reveal. A ScrollTrigger SECTION-ENTRANCE reveal (the §1.4
  // "curtain rises" — NEVER a between-pages transition; owner hates the route
  // swipe). Opt-in via .pingo-curtain (class_name hook). The band un-clips from
  // the bottom up (clip-path inset top 100%→0) with a small lift, once. Initial
  // clipped state + reduced-motion reveal live in pingo-custom.css gated on
  // .pingo-js. Mobile-inclusive (cheap GPU clip+transform).
  function animateCurtains(root) {
    if (prefersReducedMotion) return; // CSS un-clips, no tween
    root.querySelectorAll('.pingo-curtain').forEach(function (el) {
      if (!bind(el)) return;
      gsap.fromTo(el,
        { clipPath: 'inset(100% 0 0 0)', y: 24 },
        {
          clipPath: 'inset(0% 0 0 0)', y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: START, once: true }
        }
      );
    });
  }

  // Headlines wait on fonts; everything else runs immediately.
  function whenFontsReady(cb) {
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(cb); }
    else { cb(); }
  }

  function init() {
    animatePalette(document);
    initMotionPresets(document);
    initHeroFade(document);
    initPatternDrift(document);
    initMushroomSprout(document);
    initButtonFeedback();
    initCardHover();
    initDrawSVG(document);
    animateCurtains(document);
    whenFontsReady(function () {
      animateHeadlines(document);
      // Defer the refresh off the synchronous path: animateHeadlines' SplitText just
      // mutated the DOM, so a same-tick ScrollTrigger.refresh() forces a layout recalc
      // (measured ~90ms under 4× CPU — a long task around load). rAF lets the browser
      // flush layout first, so refresh reads a settled layout (no forced reflow). The
      // 1-frame delay in trigger positions is imperceptible (fires at fonts.ready,
      // before any interaction).
      requestAnimationFrame(function () { ScrollTrigger.refresh(); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor / dynamic section loads: bind freshly-added elements (DONE
  // attr dedupes existing ones) and recompute trigger positions.
  document.addEventListener('shopify:section:load', function () {
    animatePalette(document);
    initMotionPresets(document);
    initHeroFade(document);
    initPatternDrift(document);
    initMushroomSprout(document);
    initDrawSVG(document);
    animateCurtains(document);
    whenFontsReady(function () { animateHeadlines(document); });
    // Same deferral as init — the section:load handler just bound new tweens
    // (initMotionPresets/curtains write styles), so refresh off the sync path.
    requestAnimationFrame(function () { ScrollTrigger.refresh(); });
  });

  /*
    Results re-render (native facets, sort, and the pingo-tags pill row) —
    recompute trigger positions.

    Horizon filters through the Section Rendering API + morph, which changes the
    page height WITHOUT ever firing `shopify:section:load` on the storefront. So
    every ScrollTrigger start/end computed at load silently goes stale by however
    much the page grew or shrank, and nothing recomputed them.

    The bug this fixes (verified live 2026-07-31, /collections/jogos): filtering
    24 products down to 3 shortens the page ~3500px. The footer mushroom trigger
    kept its load-time `start: 4185` when the correct value was 679 — a 3506px
    drift — so it reported `isActive: false` while the footer was plainly on
    screen, and `initMushroomSprout` held `--pingo-mush-play: paused`. Because the
    sprout keyframe is declared `infinite both`, a paused item renders whatever
    phase it froze at, and the keyframe's 96-100% window is `opacity: 0` — so the
    mushrooms DISAPPEARED rather than merely stopping. A single refresh flips
    isActive and they resume, which is what proved the cause was positional.

    Only facets that change the RESULT COUNT drift the page (Availability, Price,
    a tag pill); sorting reorders without resizing, so it is harmless either way.

    Names: the `@shopify/events` namespaced pair is what Horizon 4.1.x actually
    dispatches (`StandardEvents.collectionUpdate === 'shopify:collection:update'`).
    The legacy unprefixed pair is kept so this survives a theme rollback; the
    pending-flag collapses same-tick duplicates if a theme ever emits both.

    Timing: the event's promise resolves AFTER the morph, and the refresh is then
    deferred a frame so it reads a settled layout instead of forcing a reflow —
    the same reasoning as the deferral in init().
  */
  var refreshPending = false;
  function refreshAfterResultsRender() {
    if (refreshPending) return;
    refreshPending = true;
    requestAnimationFrame(function () {
      refreshPending = false;
      ScrollTrigger.refresh();
    });
  }

  [
    'shopify:collection:update',
    'shopify:search:update',
    'collection:update',
    'search:update',
  ].forEach(function (name) {
    document.addEventListener(name, function (event) {
      var promise = (event.detail && event.detail.promise) || event.promise || null;
      if (promise && typeof promise.then === 'function') {
        promise.then(refreshAfterResultsRender).catch(refreshAfterResultsRender);
      } else {
        // No promise on the event — settle after the render would normally land.
        setTimeout(refreshAfterResultsRender, 350);
      }
    });
  });

  /*
    ...and the catch-all, because the events above do NOT cover every case.

    `blocks/pingo-tags.liquid`'s pill row drives `sectionRenderer` directly rather
    than going through `facets.js`, so it dispatches NO event at all — and it is
    the single most likely way a shopper shrinks this page. Relying on the event
    listeners alone left the original bug fully intact on the pill path (measured:
    trigger.start still 4185 after a pill click). Pagination's "view more" is the
    same shape — it grows the page with no facet event.

    So watch the thing that actually matters: the height of the content column.
    Any cause — pill, facet, sort, pagination, a lazily-loaded image reflowing a
    section — lands here.

    The 100px threshold keeps this off the hot path: entrance tweens and the
    header's own show/hide nudge the height by a few pixels every frame, and
    refreshing on those would fight the very animations it exists to serve. A
    filter that meaningfully moves the footer always clears 100px (the measured
    case was 3506px). Trailing debounce so a multi-step reflow refreshes once.
  */
  var main = document.getElementById('MainContent');
  if (main && typeof ResizeObserver === 'function') {
    var lastHeight = Math.round(main.getBoundingClientRect().height);
    var heightTimer = null;
    new ResizeObserver(function (entries) {
      var h = Math.round(entries[0].contentRect.height);
      if (Math.abs(h - lastHeight) < 100) return;
      lastHeight = h;
      clearTimeout(heightTimer);
      heightTimer = setTimeout(refreshAfterResultsRender, 200);
    }).observe(main);
  }
}());
